import moment from 'moment-timezone'
import _ from 'lodash'
import _round from 'lodash.round'
import { formatCurrency, formatDate, formatTime } from '@smarttransit/common'
import { addParamsToLocation } from '@smarttransit/common-client'
import { addAlert, isAuthorized } from '../../../../utilities/helpers'

import {
  getPayoutReportStatus,
  getPayoutReportVehicleBreakdown,
  requestPayoutReport,
  getTransportationUserTransactionLog, markAsPaidToOwner,
  payOwnerAndCheckUntilComplete
} from '../../../../services/transportation-user-transaction-logs-service'

import { getTransportationOwnerMomoAndMoneyAccounts } from '../../../../services/transportation-owner-users-service'
import { getUser } from '../../../../services/users-service'
import { getCachedMoneyAccountTypes } from '../../../../services/st-settings-service'

const REPORT_TIMEZONE = 'Africa/Accra'

export default {
  name: 'transportation-accounts-payouts-payout',
  props: {
    signedInUser: Object,
    selectedAccount: Object,
    agencies: Array
  },
  data () {
    return {
      apiInProgress: false,
      dateFrom: null,
      dateFromMenu: null,
      dateTo: null,
      dateToMenu: null,
      currentPayout: null,
      report: null,
      reportError: '',
      agencyId: 'STRANSITGHA',
      moneyAccountId: null,
      transportationMoneyAccounts: [],
      markPaidApiInProgress: false,
      scheduledJobId: null,
      statusPollTimeoutHandle: null
    }
  },
  mounted: function () {
    this.$nextTick(async () => {
      if (this.$route.query.agencyId) {
        this.agencyId = this.$route.query.agencyId
      }

      if (this.$route.params.payoutId && this.$route.params.payoutId !== '0') {
        this.getCurrentPayout(this.$route.params.payoutId)
      } else {
        if (this.$route.query.dateFrom) {
          this.dateFrom = this.$route.query.dateFrom
        }

        if (this.$route.query.dateFrom && this.$route.query.dateTo) {
          this.dateTo = this.$route.query.dateTo
        }

        if (this.$route.query.moneyAccountId) {
          this.moneyAccountId = this.$route.query.moneyAccountId
        }

        const moneyAccountTypes = await getCachedMoneyAccountTypes()
        let validTransportationOwnerUserIds = null

        if (!isAuthorized(this.$props.signedInUser, 'admin')) {
          validTransportationOwnerUserIds = this.$props.signedInUser.transportationOwnerUsers.filter((o) => (o.transportationOwnerId === this.$props.selectedAccount.id)).map((o) => o.id)
        }

        const { transportationMoneyAccounts, transportationMomoAccounts } = await getTransportationOwnerMomoAndMoneyAccounts({ transportationOwnerId: this.$route.params.accountId, transportationOwnerUserIds: validTransportationOwnerUserIds })
        const moneyAccountLogos = this.$store.getters.getMoneyAccountLogos

        if (transportationMomoAccounts.length || transportationMoneyAccounts.length) {
          this.transportationMoneyAccounts = transportationMomoAccounts.map((o) => {
            return {
              text: o.transportationUserIdentifier.phoneNumber,
              metadata: {
                src: moneyAccountLogos ? moneyAccountLogos[o.moneyAccountTypeId] : null,
                moneyAccountTypeId: o.moneyAccountTypeId,
                moneyAccountTypeCategory: moneyAccountTypes.find((m) => m.typeId === o.moneyAccountTypeId)?.typeCategory
              },
              value: o.id
            }
          })

          this.transportationMoneyAccounts = this.transportationMoneyAccounts.concat(transportationMoneyAccounts.map((o) => {
            return {
              text: o.externalId,
              metadata: {
                icon: this.$store.getters.getMoneyAccountTypes.find((m) => m.value === o.moneyAccountTypeId).metadata.icon,
                moneyAccountTypeId: o.moneyAccountTypeId,
                accountMetadata: o.accountMetadata,
                moneyAccountTypeCategory: moneyAccountTypes.find((m) => m.typeId === o.moneyAccountTypeId)?.typeCategory
              },
              value: o.id
            }
          }))

          if (this.transportationMoneyAccounts.length === 1) {
            this.moneyAccountId = this.transportationMoneyAccounts[0].value
          }

          // generate report based on dates provided
          if (this.dateFrom && this.agencyId) {
            this.getPendingPayoutReport()
          }
        }
      }
    })
  },
  watch: {
    dateFrom (val) {
      this.lastParams = addParamsToLocation({ dateFrom: val }, this.$route, this.lastParams)
    },
    dateTo (val) {
      this.lastParams = addParamsToLocation({ dateTo: val }, this.$route, this.lastParams)
    },
    moneyAccountId (val) {
      this.lastParams = addParamsToLocation({ moneyAccountId: val }, this.$route, this.lastParams)
    }
  },
  computed: {
    computedDateFromFormatted () {
      return this.dateFrom ? formatDate(this.dateFrom) : ''
    },
    computedDateToFormatted () {
      return this.dateTo ? formatDate(this.dateTo) : ''
    },
    computedDateFromMax () {
      const fromDateAsMoment = this.dateFrom ? moment(this.dateFrom, 'Y-MM-DD') : null
      const toDateAsMoment = this.dateTo ? moment(this.dateTo, 'Y-MM-DD') : null

      if (toDateAsMoment && (!fromDateAsMoment || fromDateAsMoment.valueOf() <= toDateAsMoment.valueOf())) {
        return toDateAsMoment.subtract(1, 'day').format('Y-MM-DD')
      }

      return ''
    },
    computedDateToMin () {
      const fromDateAsMoment = this.dateFrom ? moment(this.dateFrom, 'Y-MM-DD') : null
      const toDateAsMoment = this.dateTo ? moment(this.dateTo, 'Y-MM-DD') : null

      if (fromDateAsMoment && (!toDateAsMoment || fromDateAsMoment.valueOf() <= toDateAsMoment.valueOf())) {
        return fromDateAsMoment.add(1, 'day').format('Y-MM-DD')
      }

      return ''
    },
    isSuperAdmin () {
      return isAuthorized(this.$props.signedInUser, 'superadmin')
    },
    currentSelectedAccount () {
      return this.$props.selectedAccount
    },
    currentAgencies () {
      return this.$props.agencies
    },
    currentMomoAccountLabel () {
      if (this.transportationMoneyAccounts.length && this.moneyAccountId) {
        return this.transportationMoneyAccounts.find(o => o.value === this.moneyAccountId).text
      }

      return ''
    },
    currentCategoryType () {
      if (this.transportationMoneyAccounts.length && this.moneyAccountId) {
        return this.transportationMoneyAccounts.find(o => o.value === this.moneyAccountId)?.metadata?.moneyAccountTypeCategory || ''
      }

      return ''
    },
    currentReportDateRangeLabel () {
      if (!this.report) {
        return ''
      }

      const reportKeys = Object.keys(this.report)
      if (!reportKeys.length) {
        return ''
      }

      const reportItem = this.report[reportKeys[0]]
      return reportItem && reportItem.reportDateRangeLabel ? reportItem.reportDateRangeLabel : ''
    }
  },
  methods: {
    clearStatusPollTimeout () {
      if (this.statusPollTimeoutHandle) {
        clearTimeout(this.statusPollTimeoutHandle)
        this.statusPollTimeoutHandle = null
      }
    },
    isReportProcessingStatus (status) {
      return status === 'processing' || status === 'pending'
    },
    getSelectedMoneyAccount () {
      return this.transportationMoneyAccounts.find(o => o.value === this.moneyAccountId)
    },
    getAgencyRecord (agencyId) {
      return (this.$props.agencies || []).find((agency) => agency.value === agencyId) || null
    },
    formatReportDateRangeLabel ({ dateStart, dateEnd }) {
      if (!dateStart) {
        return ''
      }

      const timezoneLabel = moment(dateStart).tz(REPORT_TIMEZONE).format('z')
      const startLabel = formatDate(dateStart, REPORT_TIMEZONE)
      const endLabel = dateEnd ? formatDate(dateEnd, REPORT_TIMEZONE) : ''
      if (!endLabel || endLabel === startLabel) {
        return `${startLabel} ${timezoneLabel}`
      }

      return `${startLabel} — ${endLabel} ${timezoneLabel}`
    },
    isStransitghaAgency () {
      return String(this.agencyId || '').toLowerCase() === 'stransitgha'
    },
    buildPayoutReportRequestParams () {
      const selectedMoneyAccount = this.getSelectedMoneyAccount()
      const isMobileMoney = selectedMoneyAccount && selectedMoneyAccount.metadata && selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'mobile_money'
      const isBank = selectedMoneyAccount && selectedMoneyAccount.metadata && selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'bank'
      const normalizedStartDate = this.dateFrom ? moment.tz(this.dateFrom, 'Y-MM-DD', REPORT_TIMEZONE).startOf('day') : null
      const normalizedEndDate = this.dateTo ? moment.tz(this.dateTo, 'Y-MM-DD', REPORT_TIMEZONE).endOf('day') : null
      const isStransitgha = this.isStransitghaAgency()
      const resolvedStartDate = normalizedStartDate || (isStransitgha && this.dateTo ? moment.tz(this.dateTo, 'Y-MM-DD', REPORT_TIMEZONE).startOf('day') : null)
      const resolvedEndDate = normalizedEndDate || (isStransitgha && this.dateFrom ? moment.tz(this.dateFrom, 'Y-MM-DD', REPORT_TIMEZONE).endOf('day') : null)

      return {
        transportationUserMomoAccountId: isMobileMoney ? this.moneyAccountId : undefined,
        transportationUserMoneyAccountId: isBank ? this.moneyAccountId : undefined,
        dateStart: resolvedStartDate ? resolvedStartDate.valueOf() : undefined,
        dateEnd: resolvedEndDate ? resolvedEndDate.valueOf() : undefined,
        agencyId: this.agencyId
      }
    },
    async applyStransitghaSummaryReport ({ report, requestParams }) {
      if (!report) {
        this.report = null
        this.reportError = 'Based on the options, no unpaid revenue were found to generate a report'
        return
      }

      this.reportError = ''
      const reportAgencyId = report.agencyId || this.agencyId
      const normalizedReport = Object.assign({}, report, {
        dateStart: requestParams.dateStart,
        dateEnd: requestParams.dateEnd
      })
      this.report = await this.marshallReport({
        [reportAgencyId]: normalizedReport
      })
      await this.fetchStransitghaVehicleBreakdown({ requestParams, reportAgencyId })
    },
    async fetchStransitghaVehicleBreakdown ({ requestParams, reportAgencyId }) {
      const result = await getPayoutReportVehicleBreakdown(requestParams)
      if (!this.report || !this.report[reportAgencyId]) {
        return
      }
      this.report[reportAgencyId].vehicleBreakdown = result && result.vehicleBreakdown ? result.vehicleBreakdown : []
      this.report = await this.marshallReport(this.report)
    },
    pollStransitghaPayoutReportStatus ({ scheduledJobId, requestParams }) {
      this.clearStatusPollTimeout()
      this.statusPollTimeoutHandle = setTimeout(async () => {
        try {
          const statusResult = await getPayoutReportStatus(Object.assign({ scheduledJobId }, requestParams))
          const status = statusResult && statusResult.status
          if (this.isReportProcessingStatus(status)) {
            this.pollStransitghaPayoutReportStatus({ scheduledJobId, requestParams })
            return
          }

          this.scheduledJobId = null
          if (status === 'completed') {
            await this.applyStransitghaSummaryReport({ report: statusResult.report, requestParams })
            this.apiInProgress = false
            return
          }

          if (status === 'failed') {
            this.reportError = statusResult && statusResult.error ? statusResult.error : 'Error in payout report generation'
          } else if (status === 'error') {
            this.reportError = statusResult && statusResult.message ? statusResult.message : 'Source records not yet available. Please try again.'
          } else {
            this.reportError = 'Error in payout report generation'
          }
          this.apiInProgress = false
        } catch (err) {
          this.reportError = `Error in payout report status: ${err && err.error ? err.error.message : (err && err.message ? err.message : JSON.stringify(err))}`
          this.apiInProgress = false
        }
      }, 4000)
    },
    async getCurrentPayout (id) {
      try {
        this.apiInProgress = true
        this.currentPayout = await getTransportationUserTransactionLog({ id, filter: { include: ['transportationUserMoneyAccount', { transportationUserMomoAccount: 'transportationUserIdentifier' }] } })

        this.report = await this.marshallReport({
          [this.currentPayout.logMetadata.agencyId]: {
            agencyId: this.currentPayout.logMetadata.agencyId,
            feePercentage: this.currentPayout.logMetadata.feePercentage,
            grossPayout: this.currentPayout.logMetadata.grossPayout,
            feeDeduction: this.currentPayout.logMetadata.feeDeduction,
            payout: this.currentPayout.amount,
            ticketCount: this.currentPayout.logMetadata.ticketCount || this.currentPayout.logMetadata.total,
            currencySymbol: this.currentPayout.logMetadata.currencySymbol,
            currencyCode: this.currentPayout.logMetadata.currencyCode,
            countryCurrencyId: this.currentPayout.countryCurrencyId,
            payoutFees: this.currentPayout.logMetadata.payoutFees || this.currentPayout.logMetadata.payoutFeeSettings,
            dateStart: this.currentPayout.logMetadata.dateStart,
            dateEnd: this.currentPayout.logMetadata.dateEnd,
            byStUserId: this.currentPayout.logMetadata.byStUserId,
            notes: this.currentPayout.logMetadata.notes
          }
        })

        this.apiInProgress = false
      } catch (err) {
        // console.error(err)
        this.apiInProgress = false
      }
    },
    async marshallReport (report) {
      const users = {}

      for (const key in report) {
        if (report[key]) {
          const agency = this.getAgencyRecord(report[key].agencyId)
          report[key].agencyLabel = agency ? agency.text : report[key].agencyId
          report[key].grossPayoutLabel = `${report[key].currencySymbol}${formatCurrency(report[key].grossPayout)}`
          report[key].payoutLabel = `${report[key].currencySymbol}${formatCurrency(report[key].payout)}`
          const feePercentage = !_.isNil(report[key].feePercentage) ? report[key].feePercentage : report[key].fee
          report[key].feePercentage = _.isNil(feePercentage) ? null : Number(feePercentage)
          report[key].feeLabel = _.isNil(report[key].feePercentage) ? 'Fixed fee' : `${_round(report[key].feePercentage * 100, 2)}%`
          report[key].feeDeductionLabel = `${report[key].currencySymbol}${formatCurrency(report[key].feeDeduction)}`
          report[key].ticketCount = !_.isNil(report[key].ticketCount) ? report[key].ticketCount : report[key].total
          report[key].reportDateRangeLabel = this.formatReportDateRangeLabel({
            dateStart: report[key].dateStart,
            dateEnd: report[key].dateEnd
          })
          const payoutFees = report[key].payoutFees || report[key].payoutFeeSettings || null
          report[key].payoutFees = payoutFees

          report[key].payoutFeesLabels = payoutFees
            ? [
              { key: 'smarttransytFee', label: 'SmartTransyt Fee' },
              { key: 'perDayDriverFee', label: 'Per-Day Driver Fee' },
              { key: 'perKwHFee', label: 'Per kWh Fee' }
            ].filter((item) => !_.isNil(payoutFees[item.key]) && Number(payoutFees[item.key]) >= 0)
              .map((item) => {
                return {
                  label: item.label,
                  value: Number(payoutFees[item.key]) || 0,
                  valueLabel: `${report[key].currencySymbol}${formatCurrency(Number(payoutFees[item.key]) || 0)}`
                }
              })
            : []

          const vehicleBreakdown = Array.isArray(report[key].vehicleBreakdown) ? report[key].vehicleBreakdown : []
          let totalVehicleGrossAmount = 0
          let totalVehicleFeeDeduction = 0
          let totalVehicleNetAmount = 0

          report[key].vehicleBreakdown = vehicleBreakdown.map((item) => {
            const grossAmount = Number(item && item.grossAmount) || 0
            const feeDeduction = Number(item && item.feeDeduction) || 0
            const resolvedNetAmount = Number(item && item.netAmount) || 0
            totalVehicleGrossAmount += grossAmount
            totalVehicleFeeDeduction += feeDeduction
            totalVehicleNetAmount += resolvedNetAmount
            return Object.assign({}, item, {
              grossAmount,
              feeDeduction,
              netAmount: resolvedNetAmount,
              grossAmountLabel: formatCurrency(grossAmount),
              feeDeductionLabel: formatCurrency(feeDeduction),
              netAmountLabel: formatCurrency(resolvedNetAmount)
            })
          })
          report[key].vehicleBreakdownTotals = {
            grossAmount: totalVehicleGrossAmount,
            feeDeduction: totalVehicleFeeDeduction,
            netAmount: totalVehicleNetAmount,
            grossAmountLabel: formatCurrency(totalVehicleGrossAmount, report[key].currencySymbol),
            feeDeductionLabel: formatCurrency(totalVehicleFeeDeduction, report[key].currencySymbol),
            netAmountLabel: formatCurrency(totalVehicleNetAmount, report[key].currencySymbol)
          }

          if (this.currentPayout) {
            report[key].dateUpdatedLabel = formatTime(this.currentPayout.dateUpdated, REPORT_TIMEZONE) + ' ' + formatDate(this.currentPayout.dateUpdated, REPORT_TIMEZONE, { displayTimezone: true })
          }

          if (report[key].byStUserId) {
            try {
              const user = users[report[key].byStUserId] || await getUser({ id: report[key].byStUserId, isDeleted: true })
              report[key].byStUserLabel = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
              users[report[key].byStUserId] = user
            } catch (err) {
              addAlert({ message: err, type: 'error' })
              report[key].byStUserLabel = `ID: ${report[key].byStUserId}`
            }
          }

          if (this.currentPayout && report[key].notes) {
            report[key].notesLabel = report[key].notes.replace(/\n/g, '<br>')
          }
        }
      }

      return report
    },
    async markAsPaid () {
      const promptResult = prompt('This will mark all tickets under this agency as paid without any system payouts.\n' +
        'This is useful when payout has been rendered outside the system.\n' +
        'Please enter a deposit/confirmation number or enter "paid" to confirm marking the set period as paid')

      if ((promptResult || '').trim()) {
        try {
          this.markPaidApiInProgress = true
          const selectedMoneyAccount = this.transportationMoneyAccounts.find(o => o.value === this.moneyAccountId)

          const confirmedTransaction = await markAsPaidToOwner({
            transportationUserMomoAccountId: selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'mobile_money' ? this.moneyAccountId : undefined,
            transportationUserMoneyAccountId: selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'bank' ? this.moneyAccountId : undefined,
            dateStart: this.dateFrom ? moment.tz(this.dateFrom, 'Y-MM-DD', REPORT_TIMEZONE).startOf('day').valueOf() : null,
            dateEnd: this.dateTo ? moment.tz(this.dateTo, 'Y-MM-DD', REPORT_TIMEZONE).endOf('day').valueOf() : null,
            agencyId: this.agencyId,
            notes: (promptResult || '').trim() === 'paid' ? '' : promptResult.trim()
          })

          addAlert({
            message: `Successfully marked as paid`,
            type: 'success',
            transient: true
          })

          this.$router.push({
            name: 'transportation-accounts-payouts-payout',
            params: { payoutId: confirmedTransaction.id }
          })
        } catch (err) {
          this.reportError = `Error in marking as paid: ${err && err.error ? err.error.message : (err && err.message ? err.message : JSON.stringify(err))}`
        } finally {
          this.markPaidApiInProgress = false
        }
      }
    },
    async requestPayout () {
      if (confirm('Please confirm making a payout to: ' + this.currentSelectedAccount.companyName + ' - ' + this.currentMomoAccountLabel)) {
        try {
          this.apiInProgress = true
          const selectedMoneyAccount = this.transportationMoneyAccounts.find(o => o.value === this.moneyAccountId)

          const confirmedTransaction = await payOwnerAndCheckUntilComplete({
            transportationUserMomoAccountId: selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'mobile_money' && this.moneyAccountId,
            transportationUserMoneyAccountId: selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'bank' && this.moneyAccountId,
            dateStart: this.dateFrom ? moment.tz(this.dateFrom, 'Y-MM-DD', REPORT_TIMEZONE).startOf('day').valueOf() : null,
            dateEnd: this.dateTo ? moment.tz(this.dateTo, 'Y-MM-DD', REPORT_TIMEZONE).endOf('day').valueOf() : null,
            agencyId: this.agencyId
          })

          addAlert({
            message: `Successfully paid owner`,
            type: 'success',
            transient: true
          })

          this.$router.push({
            name: 'transportation-accounts-payouts-payout',
            params: { payoutId: confirmedTransaction.id }
          })
        } catch (err) {
          this.reportError = `Error in payout: ${err && err.error ? err.error.message : (err && err.message ? err.message : JSON.stringify(err))}`
        } finally {
          this.apiInProgress = false
        }
      }
    },
    async getPendingPayoutReport () {
      try {
        this.reportError = ''
        this.report = null
        this.clearStatusPollTimeout()
        this.scheduledJobId = null

        if (this.moneyAccountId && this.agencyId) {
          this.apiInProgress = true
          const requestParams = this.buildPayoutReportRequestParams()
          const requestResult = await requestPayoutReport(requestParams)
          if (requestResult && requestResult.status === 'completed') {
            if (this.isStransitghaAgency()) {
              await this.applyStransitghaSummaryReport({ report: requestResult.report, requestParams })
            } else {
              this.report = await this.marshallReport(requestResult.report)

              if (!this.report) {
                this.reportError = 'Based on the options, no unpaid revenue was found to generate a report'
              }
            }
          } else if (requestResult && this.isReportProcessingStatus(requestResult.status) && requestResult.scheduledJobId) {
            this.scheduledJobId = requestResult.scheduledJobId
            this.pollStransitghaPayoutReportStatus({
              scheduledJobId: requestResult.scheduledJobId,
              requestParams
            })
            return
          } else {
            this.reportError = requestResult && requestResult.message
              ? requestResult.message
              : 'Error in payout report generation'
          }
        } else {
          const fields = [
            // { key: '"Date from"', value: this.dateFrom },
            { key: '"Agency ID"', value: this.agencyId },
            { key: '"Money account"', value: this.moneyAccountId }]
            .concat(this.isStransitghaAgency() && !this.dateFrom && !this.dateTo ? [{ key: '"Date"', value: null }] : [])
            .filter(o => !o.value)
            .map(o => o.key).join(', ')

          addAlert({
            message: `The following required field(s) are missing: ${fields}`,
            type: 'error'
          })
        }
      } catch (err) {
        // console.error(err)
        this.reportError = `Error in payout report: ${err && err.error ? err.error.message : (err && err.message ? err.message : JSON.stringify(err))}`
      } finally {
        if (!this.scheduledJobId) {
          this.apiInProgress = false
        }
      }
    },
    onFilterByAgency (val) {
      this.selectedAgency = val
    }
  },
  beforeDestroy () {
    this.clearStatusPollTimeout()

    if (this.$router.currentRoute.name !== 'transportation-accounts-payouts-payout') {
      if (this.lastParams && Object.keys(this.lastParams).length) {
        addParamsToLocation({ dateFrom: null, dateTo: null, agencyId: null, moneyAccountId: null }, this.$route, this.lastParams)
      }
    }
  }
}
